import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import invoicesService from '../services/invoicesService';
import { track } from '../observability';

const schema = z.object({
  rental_id: z.string().min(1, 'Select a rental'),
  amount: z.coerce.number().gt(0, 'Amount must be greater than 0'),
  due_date: z.string().min(1, 'Required'),
  notes: z.string().optional(),
});

const inputCls =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500';

/* Client-side preview of the server's prorated_rent_for_span (services/
   billing.py): whole anchor months (start → start+1mo−1day) at full rate,
   trailing partial month daily-prorated. The server recomputes and is
   authoritative — this only previews the same formula. */
const anchorMonthEnd = (d) => {
  const y = d.getMonth() === 11 ? d.getFullYear() + 1 : d.getFullYear();
  const m = (d.getMonth() + 1) % 12;
  const dim = new Date(y, m + 1, 0).getDate();
  const next = new Date(y, m, Math.min(d.getDate(), dim));
  next.setDate(next.getDate() - 1);
  return next;
};
const proratedRent = (rate, startIso, endIso) => {
  let cursor = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || end < cursor) return 0;
  let total = 0;
  for (;;) {
    const me = anchorMonthEnd(cursor);
    if (me <= end) {
      total += rate;
      cursor = new Date(me);
      cursor.setDate(cursor.getDate() + 1);
      if (cursor > end) break;
    } else {
      const spanDays = Math.round((end - cursor) / 864e5) + 1;
      const monthDays = Math.round((me - cursor) / 864e5) + 1;
      total += (rate * spanDays) / monthDays;
      break;
    }
  }
  return Math.round(total * 100) / 100;
};

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const InvoiceForm = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const { id: invoiceId } = useParams();
  const isEdit = Boolean(invoiceId);
  const [rentals, setRentals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [fees, setFees] = useState([]);
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  // The two OPTIONAL amount sources (owner, 2026-08-10): bill rent through
  // a date (amount calculated from the monthly rate) OR a catalog fee
  // (amount populated from the fee). Picking one clears the other; typing
  // an amount by hand clears the bill-through (fees are just a prefill).
  const [billTo, setBillTo] = useState('');
  // Edit mode: bill_to is only SENT when the operator changed it — saving
  // an unrelated edit must never silently re-price a hand-adjusted amount.
  const [initialBillTo, setInitialBillTo] = useState('');
  const [feeId, setFeeId] = useState('');
  // Edit mode: the loaded invoice's period fields (bill-through is only
  // editable on a rent invoice that carries a billing period).
  const [editInvoice, setEditInvoice] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      rental_id: '',
      amount: '',
      due_date: new Date(Date.now() + 7 * 864e5).toISOString().split('T')[0],
      notes: '',
    },
  });
  const rentalId = watch('rental_id');
  const selectedRental = rentals.find((r) => r._id === rentalId);

  const fetchData = useCallback(async () => {
    if (!facilityId) return;
    try {
      const [rentalsRes, customersRes, feesRes] = await Promise.all([
        // Trailing slashes — the no-slash form draws a 307 redirect and
        // SAFARI STRIPS Authorization on redirects (phone "Not
        // authenticated", 2026-08-02).
        apiClient.get(`/api/facilities/${facilityId}/rentals/`),
        apiClient.get(`/api/facilities/${facilityId}/customers/`),
        apiClient.get(`/api/facilities/${facilityId}/fees/`),
      ]);
      // FULL list — filtering to Active here made a Delinquent rental's
      // invoice edit show an empty picker and a $0 rate (no auto-compute).
      // Delinquent/Lien tenants are exactly who manual invoices target.
      setRentals(rentalsRes.data);
      setCustomers(customersRes.data);
      setFees(feesRes.data || []);
    } catch (err) {
      console.error('Error loading rentals/customers/fees:', err);
    }
  }, [facilityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Edit mode: load the existing invoice and prefill the form.
  useEffect(() => {
    if (!facilityId || !isEdit) return;
    (async () => {
      try {
        const { data } = await invoicesService.getById(facilityId, invoiceId);
        setEditInvoice(data);
        reset({
          rental_id: data.rental_id || '',
          amount: data.amount ?? '',
          due_date: (data.due_date || '').split('T')[0],
          notes: data.notes || '',
        });
        if (data.type === 'rent' && data.period_start && data.period_end) {
          const end = String(data.period_end).split('T')[0];
          setBillTo(end);
          setInitialBillTo(end);
        }
      } catch (err) {
        setServerError('Could not load this invoice.');
      }
    })();
  }, [facilityId, isEdit, invoiceId, reset]);

  const customerName = (id) => {
    const c = customers.find((x) => x._id === id);
    return c ? c.full_name : 'Unknown';
  };

  // Bill-through baseline: create bills from the day AFTER the rental's
  // current billed-through (or its start date if never billed). Edit
  // re-prices a rent invoice from its OWN fixed period_start; an invoice
  // without a period converts to a rent invoice from the rental's
  // billed-through — so ANY unpaid invoice can extend months ahead
  // (pay-ahead, owner 2026-08-10).
  const editRental = isEdit ? rentals.find((r) => r._id === editInvoice?.rental_id) : null;
  const baselineFromRental = (r) =>
    r
      ? (r.billed_to
          ? addDays(String(r.billed_to).slice(0, 10), 1)
          : String(r.start_date || '').slice(0, 10) || null)
      : null;
  const billFromIso = isEdit
    ? (editInvoice?.type === 'rent' && editInvoice?.period_start
        ? String(editInvoice.period_start).slice(0, 10)
        : baselineFromRental(editRental))
    : baselineFromRental(selectedRental);
  const billToEditable = isEdit ? Boolean(editInvoice?.rental_id) : Boolean(selectedRental);
  const monthlyRate = Number(
    (isEdit ? editRental : selectedRental)?.monthly_rate || 0,
  );

  const onBillToChange = (value) => {
    setBillTo(value);
    if (value) setFeeId('');
  };

  // Auto-compute the amount whenever an operator-chosen bill-through and a
  // resolved rate are both present — including when the rentals list (the
  // rate source) loads AFTER the date was picked. Skips the untouched
  // edit-mode initial value so a hand-adjusted amount is never overwritten.
  useEffect(() => {
    if (!billTo || billTo === initialBillTo) return;
    if (billFromIso && monthlyRate > 0) {
      setValue('amount', proratedRent(monthlyRate, billFromIso, billTo), {
        shouldValidate: true,
      });
    }
  }, [billTo, initialBillTo, billFromIso, monthlyRate, setValue]);

  const onFeeChange = (value) => {
    setFeeId(value);
    if (value) {
      setBillTo('');
      const fee = fees.find((f) => f._id === value);
      if (fee) {
        setValue('amount', Number(fee.amount || 0), { shouldValidate: true });
        if (!watch('notes')) setValue('notes', fee.name || '');
      }
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setServerError(null);
    try {
      if (isEdit) {
        // Edit: only the editable fields; rental stays fixed (server moves
        // the rental balance by the amount delta). bill_to re-prices the
        // period server-side (the amount we send is a preview of the same
        // formula).
        await invoicesService.update(facilityId, invoiceId, {
          amount: Number(data.amount),
          due_date: data.due_date,
          notes: data.notes || null,
          ...(billTo && billToEditable && billTo !== initialBillTo
            ? { bill_to: billTo }
            : {}),
        });
        track('invoice_updated', { facility_id: facilityId });
      } else {
        const rental = rentals.find((r) => r._id === data.rental_id);
        if (!rental) {
          setServerError('Selected rental not found.');
          setSaving(false);
          return;
        }
        await invoicesService.create(facilityId, {
          customer_id: rental.customer_id,
          rental_id: data.rental_id,
          amount: Number(data.amount),
          due_date: data.due_date,
          notes: data.notes || null,
          ...(billTo ? { bill_to: billTo } : {}),
        });
        track('invoice_created', { facility_id: facilityId });
      }
      navigate('/invoices');
    } catch (err) {
      setServerError(
        err.response?.data?.detail ||
          (isEdit ? 'Could not update invoice.' : 'Could not create invoice.'),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
        <p className="text-[13px] text-slate-500 mt-1">
          Create a facility in Settings first.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">
          {isEdit ? 'Edit Invoice' : 'New Invoice'}
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Tax is applied automatically from facility settings; the rental balance
          is updated by the amount change. Only unpaid invoices (draft, sent,
          overdue) can be edited.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-xl border border-slate-200/80 bg-white p-6 space-y-4"
      >
        <div>
          <label className="block text-[12px] font-medium text-slate-500 mb-1">
            Rental
          </label>
          {isEdit ? (
            <input
              className={`${inputCls} bg-slate-50 text-slate-500 cursor-not-allowed`}
              disabled
              readOnly
              value={
                editRental
                  ? `${customerName(editRental.customer_id)} — Unit ${editRental.unit_number || editRental.unit_id} ($${editRental.monthly_rate}/mo)`
                  : editInvoice
                  ? customerName(editInvoice.customer_id)
                  : 'Loading…'
              }
            />
          ) : (
            <select className={inputCls} {...register('rental_id')}>
              <option value="">Select a rental…</option>
              {rentals
                .filter((r) => r.status !== 'Ended')
                .map((r) => (
                  <option key={r._id} value={r._id}>
                    {customerName(r.customer_id)} — Unit {r.unit_number || r.unit_id} (${r.monthly_rate}/mo)
                  </option>
                ))}
            </select>
          )}
          {errors.rental_id && (
            <p className="text-[12px] text-red-500 mt-1">{errors.rental_id.message}</p>
          )}
          {!isEdit && selectedRental && (
            <p className="text-[11px] text-slate-400 mt-1">
              Billed through:{' '}
              {selectedRental.billed_to
                ? String(selectedRental.billed_to).slice(0, 10)
                : 'never billed'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Bill rent through (optional)
            </label>
            <input
              type="date"
              className={inputCls}
              value={billTo}
              min={billFromIso || undefined}
              disabled={!billToEditable}
              onChange={(e) => onBillToChange(e.target.value)}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              {billToEditable
                ? `Amount is calculated from the monthly rate${billFromIso ? ` starting ${billFromIso}` : ''} — extend it to bill months ahead.`
                : 'Pick a rental first.'}
            </p>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Fee from catalog (optional)
            </label>
            <select
              className={inputCls}
              value={feeId}
              onChange={(e) => onFeeChange(e.target.value)}
            >
              <option value="">Select a fee…</option>
              {fees.map((f) => (
                <option key={f._id} value={f._id}>
                  {f.name} — ${Number(f.amount || 0).toFixed(2)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">
              Populates the amount from the fee catalog.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Amount (pre-tax, USD)
            </label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              {...register('amount', {
                onChange: () => {
                  // Hand-typed amount: the bill-through no longer prices
                  // this invoice — drop it (edit: revert to the stored
                  // period so nothing is sent; fees are just a prefill).
                  if (billTo) setBillTo(isEdit ? initialBillTo : '');
                },
              })}
            />
            {billTo && (
              <p className="text-[11px] text-slate-400 mt-1">
                Calculated from ${monthlyRate.toFixed(2)}/mo — the server
                recomputes on save.
              </p>
            )}
            {errors.amount && (
              <p className="text-[12px] text-red-500 mt-1">{errors.amount.message}</p>
            )}
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Due date
            </label>
            <input type="date" className={inputCls} {...register('due_date')} />
            {errors.due_date && (
              <p className="text-[12px] text-red-500 mt-1">{errors.due_date.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-medium text-slate-500 mb-1">
            Notes (optional)
          </label>
          <textarea rows="2" className={inputCls} {...register('notes')} />
        </div>

        {serverError && <p className="text-[13px] text-red-500">{serverError}</p>}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create invoice')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="text-[13px] text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
